import React, { useState } from "react";
import { Box, Typography, Paper, Grid2 as Grid, Card, CardContent, CardActionArea } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import LinkIcon from "@mui/icons-material/Link";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useInvalidateContacts } from "../hooks/crm/useContacts";
import { ContactCreateChat } from "../components/CRM/contacts/ContactCreateChat";
import { MiniUploadFile } from "../components/PersonFinancialData/MiniFileUpload";
import ConsentActionsTable from "../components/Users/ConsentActionsTable";
import PreadviceTasksTable from "../components/Tasks/PreadviceTasksTable";

enum ConsultingOption {
  UPLOAD_ZIP = "UPLOAD_ZIP",
  SEND_LINK = "SEND_LINK",
  EXISTING_CLIENT = "EXISTING_CLIENT",
}

export const NewConsultingPage: React.FC = () => {
  const [selectedOption, setSelectedOption] = useState<ConsultingOption | null>(null);
  const invalidateContacts = useInvalidateContacts();

  const handleOptionClick = (option: ConsultingOption) => {
    setSelectedOption(option);
  };

  const renderContent = () => {
    if (!selectedOption) return null;

    switch (selectedOption) {
      case ConsultingOption.SEND_LINK:
        return (
          <Box sx={{ padding: 0, marginTop: 4 }}>
            <ContactCreateChat onCancel={() => setSelectedOption(null)} onSuccess={() => {
              console.log("Contact created successfully");
              // TODO: Navigate to link generation or other flow
            }} />
          </Box>
        );

      case ConsultingOption.UPLOAD_ZIP:
        return (
          <Box sx={{ padding: 4, direction: "rtl", marginTop: 4 }}>
            <Paper elevation={3} sx={{ padding: 4, maxWidth: 800, margin: "0 auto" }}>
              <Typography variant="h4" align="center" sx={{ marginBottom: 3 }}>
                העלאת קובץ ZIP של טרום ייעוץ
              </Typography>
              <Typography variant="body1" color="text.secondary" align="center" sx={{ marginBottom: 4, direction: "rtl" }}>
                העלה קובץ ZIP המכיל את מסמכי הטרום ייעוץ של הלקוח
              </Typography>
              <Box sx={{ marginTop: 4 }}>
                <MiniUploadFile
                  isPreAdvice={true}
                  onFileUploaded={(file: File) => {
                    console.log("Upload complete:", file.name);
                    invalidateContacts();
                  }}
                />
              </Box>
            </Paper>
          </Box>
        );

      case ConsultingOption.EXISTING_CLIENT:
        return (
          <Box sx={{ padding: 4, marginTop: 4 }}>
            <Paper elevation={3} sx={{ padding: 4, margin: "0 auto" }}>
              <Typography variant="h4" align="center" sx={{ marginBottom: 3 }}>
                הזמן טרום ייעוץ ללקוח קיים
              </Typography>
              <Box sx={{ marginTop: 4 }}>
                <ConsentActionsTable
                  onRequestPreAdvice={(contactId) => {
                  
                    // TODO: Navigate to pre-advice request flow
                  }}
                />
              </Box>
              <Box sx={{ marginTop: 4 }}>
                <PreadviceTasksTable />
              </Box>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ padding: 2, minHeight: "80vh" }}>
      <Box sx={{ maxWidth: 1200, margin: "0 auto", width: "100%" }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ marginBottom: 6 }}>
          יעוץ חדש
        </Typography>
      

        <Grid container spacing={4} justifyContent="center">
          {/* Option 1: Upload ZIP */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              elevation={selectedOption === ConsultingOption.UPLOAD_ZIP ? 8 : 4}
              sx={{
                height: "100%",
                minHeight: 280,
                transition: "all 0.3s",
                border: selectedOption === ConsultingOption.UPLOAD_ZIP ? 2 : 0,
                borderColor: "primary.main",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: 8,
                },
              }}
            >
              <CardActionArea
                onClick={() => handleOptionClick(ConsultingOption.UPLOAD_ZIP)}
                sx={{ height: "100%", padding: 3 }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    gap: 2,
                  }}
                >
                  <UploadFileIcon sx={{ fontSize: 80, color: "primary.main" }} />
                  <Typography variant="h5" component="div" align="center" gutterBottom>
                    טען קובץ
                  </Typography>
                  <Typography variant="body1" color="text.secondary" align="center">
                    העלה קובץ ZIP של טרום ייעוץ עבור לקוח חדש
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          {/* Option 2: Send Link to Client */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              elevation={selectedOption === ConsultingOption.SEND_LINK ? 8 : 4}
              sx={{
                height: "100%",
                minHeight: 280,
                transition: "all 0.3s",
                border: selectedOption === ConsultingOption.SEND_LINK ? 2 : 0,
                borderColor: "success.main",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: 8,
                },
              }}
            >
              <CardActionArea
                onClick={() => handleOptionClick(ConsultingOption.SEND_LINK)}
                sx={{ height: "100%", padding: 3 }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    gap: 2,
                  }}
                >
                  <LinkIcon sx={{ fontSize: 80, color: "success.main" }} />
                  <Typography variant="h5" component="div" align="center" gutterBottom>
                   צור לקוח חדש
                  </Typography>
                  <Typography variant="body1" color="text.secondary" align="center">
                    צור לקוח חדש ושלח לו לינק למילוי פרטים
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          {/* Option 3: Existing Client */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              elevation={selectedOption === ConsultingOption.EXISTING_CLIENT ? 8 : 4}
              sx={{
                height: "100%",
                minHeight: 280,
                transition: "all 0.3s",
                border: selectedOption === ConsultingOption.EXISTING_CLIENT ? 2 : 0,
                borderColor: "info.main",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: 8,
                },
              }}
            >
              <CardActionArea
                onClick={() => handleOptionClick(ConsultingOption.EXISTING_CLIENT)}
                sx={{ height: "100%", padding: 3 }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    gap: 2,
                  }}
                >
                  <PersonAddIcon sx={{ fontSize: 80, color: "info.main" }} />
                  <Typography variant="h5" component="div" align="center" gutterBottom>
                    לקוח קיים
                  </Typography>
                  <Typography variant="body1" color="text.secondary" align="center">
                    הזמן טרום ייעוץ ללקוח קיים במערכת
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>

        {/* Render selected content below the cards */}
        {renderContent()}
      </Box>
    </Box>
  );
};
export {};
